import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { GraduationCap, Search, BookOpen, Sparkles, Mail } from "lucide-react";

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric", month: "short", day: "numeric",
        });
    } catch {
        return iso;
    }
}

/**
 * Instructor — read-only list of every student enrolled in any program
 * the instructor owns. Scoped server-side via `/api/instructor/students`.
 * Tapping a row opens the student dossier at `/instructor/students/:id`.
 */
export default function InstructorStudents() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");

    useEffect(() => {
        let live = true;
        api.get("/instructor/students")
            .then((r) => {
                if (live) setRows(r.data || []);
            })
            .finally(() => live && setLoading(false));
        return () => {
            live = false;
        };
    }, []);

    const filtered = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(
            (s) =>
                s.name?.toLowerCase().includes(q) ||
                s.email?.toLowerCase().includes(q) ||
                s.student_id?.toLowerCase().includes(q) ||
                s.enrollments?.some((e) => e.program_title?.toLowerCase().includes(q)),
        );
    }, [rows, filter]);

    return (
        <div
            className="max-w-7xl mx-auto px-6 py-10 pb-24"
            data-testid="instructor-students-page"
        >
            <Link
                to="/instructor"
                className="text-sm text-muted-foreground hover:text-primary"
            >
                ← Back to my programs
            </Link>
            <div className="mt-2 mb-8">
                <div className="inline-flex items-center gap-1.5 chip chip-accent">
                    <GraduationCap className="h-3 w-3" />
                    My roster
                </div>
                <h1 className="font-display text-3xl md:text-4xl text-foreground mt-3 tracking-tight">
                    My students
                </h1>
                <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">
                    Every student enrolled in a program you teach. Tap a student
                    to see their full progress profile.
                </p>
            </div>

            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div className="relative w-full sm:w-80">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        placeholder="Search name, email, student ID or program…"
                        className="pl-9"
                        data-testid="instructor-students-filter"
                    />
                </div>
                <p className="text-xs text-muted-foreground">
                    Showing <strong>{filtered.length}</strong> of {rows.length}
                </p>
            </div>

            <Card className="surface-card border-border">
                <CardContent className="p-0">
                    <Table className="data-table">
                        <TableHeader>
                            <TableRow>
                                <TableHead>Student</TableHead>
                                <TableHead>Programs</TableHead>
                                <TableHead className="w-32">Progress</TableHead>
                                <TableHead className="w-32">Last seen</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={4} className="text-center py-12">
                                        <div className="text-sm text-muted-foreground">
                                            Loading…
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : filtered.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} className="text-center py-12">
                                        <div className="empty-state !py-8">
                                            <div className="empty-icon-wrap">
                                                <Sparkles className="h-8 w-8 text-primary" />
                                            </div>
                                            <p className="font-display text-lg text-foreground">
                                                {filter
                                                    ? "No matches"
                                                    : "No students enrolled yet"}
                                            </p>
                                            <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                                                {filter
                                                    ? "Try a different search term."
                                                    : "Once students enrol in your programs, they'll show up here."}
                                            </p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filtered.map((s) => {
                                    const totalChapters = (s.enrollments || []).reduce(
                                        (acc, e) => acc + (e.total_chapters || 0),
                                        0,
                                    );
                                    const passedChapters = (s.enrollments || []).reduce(
                                        (acc, e) => acc + (e.chapters_completed || 0),
                                        0,
                                    );
                                    const overall = totalChapters
                                        ? Math.round((passedChapters / totalChapters) * 100)
                                        : 0;
                                    return (
                                        <TableRow
                                            key={s.id}
                                            data-testid={`instructor-student-row-${s.id}`}
                                        >
                                            <TableCell>
                                                <Link
                                                    to={`/instructor/students/${s.id}`}
                                                    className="flex items-center gap-3 group"
                                                    data-testid={`instructor-student-link-${s.id}`}
                                                >
                                                    <div className="h-9 w-9 rounded-full bg-accent/15 text-accent-foreground flex items-center justify-center font-display text-sm ring-1 ring-accent/30">
                                                        {s.name?.charAt(0).toUpperCase() || "?"}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="font-medium text-foreground truncate group-hover:text-primary">
                                                            {s.name}
                                                        </div>
                                                        <div className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                                                            <Mail className="h-3 w-3" />
                                                            {s.email}
                                                            {s.student_id ? (
                                                                <span className="text-[10px] ml-2 font-mono text-muted-foreground">
                                                                    {s.student_id}
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                </Link>
                                            </TableCell>
                                            <TableCell>
                                                {(s.enrollments || []).length === 0 ? (
                                                    <span className="text-xs text-muted-foreground italic">
                                                        —
                                                    </span>
                                                ) : (
                                                    <div className="flex flex-wrap gap-1.5 max-w-md">
                                                        {s.enrollments.map((e) => (
                                                            <Badge
                                                                key={e.enrollment_id}
                                                                variant="outline"
                                                                className="text-[10px] gap-1"
                                                            >
                                                                <BookOpen className="h-2.5 w-2.5" />
                                                                {e.program_title}
                                                                <span className="text-muted-foreground">
                                                                    · {e.progress_percent}%
                                                                </span>
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div className="font-display text-xl text-foreground tabular-nums">
                                                    {overall}%
                                                </div>
                                                <div className="text-[10px] text-muted-foreground">
                                                    {passedChapters}/{totalChapters} chapters
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-xs text-muted-foreground">
                                                {fmtDate(s.last_seen_at)}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
}
